import * as Utils from '@js/modules/utils';
import I18n from '@js/modules/translations';

import api from '@js/actions/api';

import MapInterface from '@js/modules/maps/mapInterface';


export default class PlanMap extends MapInterface {
    _mapModel = {
        planPoiData: {},

        poiPointMarkers: [],
        poiMarkersInfo: [],
        poiMarkersPoints: [],

        poiPlanLoaded: [],
        poiAddedClicked: [],
        poiMovedClicked: [],
        poiEditClicked: [],
        poiRemovedClicked: [],
        // poiMarkerClicked: [],

        zoomOnMarker: 12,

        isPlanToStartMode: false,
        rideStartMarker: undefined,

        // Prevent click on map if marker click
        isMarkerClick: false,

        // activity points
        linePoints: [],

        poiEditButtonId: 'poi-plan-edit'
    };

    constructor(modules, params) {
        super(modules, params);

        if (!this._model.map) {
            return;
        }

        this.onMapLoaded(() => {
            this._model.map.on('click', this._addKeyPointDebounce);

            if (params.isPlanToStartMode) {
                this._mapModel.isPlanToStartMode = true;

                return;
            }

            this.fetchPlanPOIs();

            this._addPoiPopupMarkerEvents();

            if (this.hasModule('RIDE_TRACK')) {
                this.plugRidesTracks();
            }

            if (this.hasModule('DRAW_LINE')) {
                this.plugDrawLine({
                    isInteractive: !this._mapModel.isPlanToStartMode
                });
            }
        });
    }

    fetchPlanPOIs(filters) {
        const params = {
            plan: true,
            filter: filters || {}
        };

        api.get('/api/v1/plan/points-of-interest', params)
            .then((poiData) => {
                if (!poiData || Utils.isEmpty(poiData.pois)) {
                    return;
                }

                this._mapModel.planPoiData = poiData.pois;

                this._poiLoadedFuncs(this._mapModel.planPoiData);

                this._drawPois();

                this._fitMapBounds();
            });
    }

    centerPlanPoiMarker = (markerId, openPopup = false) => {
        if (this._mapModel.poiMarkersInfo.length > 0) {
            for (let markerIndex = 0; markerIndex < this._mapModel.poiMarkersInfo.length; markerIndex++) {
                const markerInfo = this._mapModel.poiMarkersInfo[markerIndex];

                if (markerInfo.data.id === markerId) {
                    this.flyTo(markerInfo.latLng, this._mapModel.zoomOnMarker);

                    this.closeAllPopups();

                    if (openPopup) {
                        this._model.hasPopup = true;

                        const markerPoint = this._mapModel.poiMarkersPoints[markerIndex];

                        this._model.openPopups.push(markerPoint.popup);

                        markerPoint.popup
                            .setLngLat(markerInfo.latLng)
                            .addTo(this._model.map);

                        const associatedRideId = markerInfo.data.associatedRides.first()?.id;
                        if (associatedRideId) {
                            this._displayPoiRideTrack(associatedRideId);
                        }
                    }
                }
            }
        }
    };

    onPoiPlanLoaded = (poiCallback) => {
        this._mapModel.poiPlanLoaded.push(poiCallback);
    };

    onPoiAddedClicked = (poiCallback) => {
        this._mapModel.poiAddedClicked.push(poiCallback);
    };

    onPoiEditClicked = (poiCallback) => {
        this._mapModel.poiEditClicked.push(poiCallback);
    };

    onPoiMovedClicked = (poiCallback) => {
        this._mapModel.poiMovedClicked.push(poiCallback);
    };

    onPoiRemovedClicked = (poiCallback) => {
        this._mapModel.poiRemovedClicked.push(poiCallback);
    };

    // onPoiMarkerClicked = (poiCallback) => {
    //     this._mapModel.poiMarkerClicked.push(poiCallback);
    // };

    drawRideStartMarker = (coordinates) => {
        this._mapModel.rideStartMarker = new MapInterface.MarkerPoint(
            this.buildMarker(
                'edit-point-marker',
                {
                    lat: coordinates.lat,
                    lng: coordinates.lng
                },
                {
                    draggable: false
                })
        );

        // add to map
        this._mapModel.rideStartMarker.marker.addTo(this._model.map);
    };

    drawPlanToStartMarker = (coordinates) => {
        if (!this._mapModel.isPlanToStartMode) {
            return;
        }

        this._model.mapBounds = null;

        this._mapModel.planPoiData = [{
            latitude: coordinates.lat,
            longitude: coordinates.lng
        }];

        this._drawPois();

        this._fitMapBounds();
    };

    goToLocation = (bounds, center, {
        showPositionMarker = false
    } = {}) => {
        if (!this._model.map) {
            return;
        }

        // Use first bounds then center
        let lat;
        let lng;
        bounds = this._buildBounds(bounds);

        if (Utils.is()
            .isObject(center)) {
            lat = center.lat;
            lng = center.lng;
        } else {
            lat = center[1];
            lng = center[0];
        }

        if ((!lat || !lng) && !bounds) {
            return;
        }

        if (showPositionMarker) {
            this.displaySearchMarker(lat, lng);
        }

        let moveToBounds;
        if (bounds) {
            moveToBounds = this.fitBounds(bounds, {zoom: this._model.searchZoom});
        }

        if (center || !moveToBounds) {
            this.flyTo({
                lat: lat,
                lng: lng
            }, this._model.searchZoom);
        }
    };

    clear = () => {
        this._clearPlanPoiMarkersPoints();

        if (this.hasModule('RIDE_TRACK')) {
            this.clearRidesTracks();
        }
    };

    /* Private Methods
    ******************** */
    _addKeyPointDebounce = Utils.debounce((event) => {
        if (this._model.isDoubleClick) {
            this._model.isDoubleClick = false;
            return;
        }

        // Avoid double click
        this._addKeyPointAtClickLoc(event.lngLat);
    }, 200);

    _addKeyPointAtClickLoc = (coordinates) => {
        if (this._mapModel.isMarkerClick) {
            this._mapModel.isMarkerClick = false;
            return;
        }

        if (this._model.openPopups.length) {
            this._model.openPopups = [];
            return;
        }

        this._poiAddedClickedFuncs(coordinates);

        this.drawPlanToStartMarker(coordinates);
    };

    _displayPoiRideTrack = (rideId) => {
        if (this.hasModule('RIDE_TRACK')) {
            this.displayRideTrack(
                rideId,
                undefined,
                {
                    onlyOneTrack: false,
                    removeIfPresent: true
                }
            );
        }
    };

    _poiLoadedFuncs = (pois) => {
        for (let i = 0, n = this._mapModel.poiPlanLoaded.length; i < n; i++) {
            this._mapModel.poiPlanLoaded[i](pois);
        }
    };

    _poiAddedClickedFuncs = (markerType, markerData) => {
        for (let i = 0, n = this._mapModel.poiAddedClicked.length; i < n; i++) {
            this._mapModel.poiAddedClicked[i](markerType, markerData);
        }
    };

    _poiEditClickedFuncs = (planPoi) => {
        for (let i = 0, n = this._mapModel.poiEditClicked.length; i < n; i++) {
            this._mapModel.poiEditClicked[i](planPoi);
        }
    };

    _poiMovedClickedFuncs = (coordinates, planPoi) => {
        for (let i = 0, n = this._mapModel.poiMovedClicked.length; i < n; i++) {
            this._mapModel.poiMovedClicked[i](coordinates, planPoi);
        }
    };

    _poiRemovedClickedFuncs = (planPoi) => {
        for (let i = 0, n = this._mapModel.poiRemovedClicked.length; i < n; i++) {
            this._mapModel.poiRemovedClicked[i](planPoi);
        }
    };

    _drawPois = () => {
        this.clear();

        this._createPoiMarkers();

        this._drawPoiMarkersPoints();

        if (this._mapModel.isPlanToStartMode) {
            return;
        }

        this._buildPoiPopups();
    };

    _createPoiMarkers = () => {
        for (let i = 0; i < this._mapModel.planPoiData.length; i++) {
            const marker = new MapInterface.MarkerInfo(
                {
                    lat: this._mapModel.planPoiData[i].latitude,
                    lng: this._mapModel.planPoiData[i].longitude
                },
                'planPoi',
                this._mapModel.planPoiData[i]
            );

            this._mapModel.poiPointMarkers.push(marker);
            this._mapModel.poiMarkersInfo.push(marker);
        }
    };

    _drawPoiMarkersPoints = () => {
        let newMarkerPoint;

        if (this._mapModel.poiMarkersInfo.length > 0) {
            for (let markerIndex = 0; markerIndex < this._mapModel.poiMarkersInfo.length; markerIndex++) {
                const markerInfo = this._mapModel.poiMarkersInfo[markerIndex];
                let markerPoiName;

                // create MarkerPoint object
                if (this._mapModel.isPlanToStartMode) {
                    markerPoiName = 'start-marker';
                } else {
                    const planPoiWithRide = markerInfo.data.associatedRides && markerInfo.data.associatedRides.length > 0;
                    const planPoiPerformed = markerInfo.data.planDateType === 'completed' || (markerInfo.data.associatedRides && markerInfo.data.associatedRides.length > 0) && markerInfo.data.associatedRides.every((ride) => !ride.planned);
                    markerPoiName = `poi-${markerInfo.data.rideType.toLowerCase()}`;
                    if (planPoiPerformed) {
                        markerPoiName += '-performed';
                    } else if (!planPoiWithRide) {
                        markerPoiName += '-planned';
                    }

                    markerPoiName += '-marker';
                }

                newMarkerPoint = new MapInterface.MarkerPoint(
                    this.buildMarker(
                        markerPoiName,
                        {
                            lat: markerInfo.latLng.lat,
                            lng: markerInfo.latLng.lng
                        },
                        {
                            draggable: true
                        }),
                    markerInfo.data && markerInfo.data.id
                );

                // add to map
                newMarkerPoint.marker.addTo(this._model.map);

                // add to array for reference
                this._mapModel.poiMarkersPoints.push(newMarkerPoint);

                // set up event handlers
                this._addMarkersPointEventHandlers(newMarkerPoint, markerInfo, markerIndex);
            }
        }
    };

    _addMarkersPointEventHandlers = (markerPoint, markerInfo, markerIndex) => {
        markerPoint.marker.getElement()
            .addEventListener('click', function (poiPoint, poiInfo /*, poiIndex */) {
                this._mapModel.isMarkerClick = true;

                this._model.openPopups.push(poiPoint.popup);

                // this._poiMarkerClickedFuncs(markerInfo.type, markerInfo.data);

                const associatedRideId = poiInfo.data.associatedRides?.first()?.id;
                if (associatedRideId) {
                    this._displayPoiRideTrack(associatedRideId);
                }
            }.bind(this, markerPoint, markerInfo, markerIndex));

        markerPoint.marker.getElement()
            .addEventListener('contextmenu', function (poiPoint, poiInfo, poiIndex) {
                this._removePoiMarker(poiPoint, poiInfo, poiIndex);
            }.bind(this, markerPoint, markerInfo, markerIndex));

        markerPoint.marker
            .on('dragend', function (poiPoint, poiInfo /*, poiIndex */) {
                const newCoordinates = poiPoint.marker.getLngLat();

                if (this._mapModel.isPlanToStartMode) {
                    this._poiMovedClickedFuncs(newCoordinates, poiInfo.data);
                } else {
                    this.alertPopup.fire({
                        title: I18n.t('js.maps.poi.plan.move'),
                        icon: 'warning',
                        showCancelButton: true,
                        cancelButtonText: I18n.t('js.helpers.buttons.cancel'),
                        confirmButtonText: I18n.t('js.helpers.buttons.apply')
                    })
                        .then((result) => {
                            if (result.isConfirmed) {
                                poiInfo.latLng = newCoordinates;

                                this._poiMovedClickedFuncs(newCoordinates, poiInfo.data);
                            } else {
                                this._drawPois();
                            }
                        });
                }
            }.bind(this, markerPoint, markerInfo, markerIndex));
    };

    _buildPoiPopups = () => {
        if (this._mapModel.poiMarkersInfo && this._mapModel.poiMarkersInfo.length > 0) {
            for (let m = 0; m < this._mapModel.poiMarkersInfo.length; m++) {
                const markerPoint = this._mapModel.poiMarkersPoints[m];
                const markerInfo = this._mapModel.poiMarkersInfo[m];
                const markerData = this._mapModel.poiMarkersInfo[m].data;

                const poiPopupContent = this._buildPoiMarkerPopup(markerPoint, markerInfo, markerData);

                if (poiPopupContent) {
                    markerPoint.popup = this.buildPopup({
                        maxWidth: 320,
                        offset: [0, -30]
                    })
                        .setHTML(poiPopupContent);

                    markerPoint.marker
                        .setPopup(markerPoint.popup);
                }
            }
        }
    };

    _buildPoiMarkerPopup = (markerPoint, markerInfo, markerData) => {
        const associatedRides = markerInfo.data.associatedRides;

        const popupContainer = document.createElement('div');
        popupContainer.className = 'poi-popup-container';

        const popupContentElement = document.createElement('div');
        popupContentElement.className = 'popup-info-box poi-popup poi-plan-popup';

        const titleElement = document.createElement('p');
        titleElement.className = 'poi-plan-title';
        titleElement.innerHTML = markerData.title;
        popupContentElement.appendChild(titleElement);

        if (markerData.description) {
            const descriptionElement = document.createElement('p');
            descriptionElement.className = 'poi-plan-description';
            descriptionElement.innerHTML = markerData.description;
            popupContentElement.appendChild(descriptionElement);
        }

        if (associatedRides.length) {
            const popupRidesDetail = document.createElement('div');
            popupRidesDetail.className = 'poi-plan-ride';
            popupRidesDetail.innerHTML = I18n.t('js.maps.marker.ride.associated_ride');

            associatedRides.forEach((ride) => {
                const popupRideButton = document.createElement('div');
                popupRideButton.className = 'details-link';
                popupRideButton.innerHTML = `<a class="button" href="${ride.link}" target="${window.innerWidth < window.settings.medium_screen ? '_self' : '_blank'}">${ride.name}</a>`;
                popupRidesDetail.appendChild(popupRideButton);
            });

            popupContentElement.appendChild(popupRidesDetail);
        } else {
            // OPTIMIZE: associated to an existing ride
            // const popupPerformedButton = document.createElement('div');
            // popupPerformedButton.id = 'poi-plan-associate';
            // popupPerformedButton.className = 'details-link';
            // popupPerformedButton.innerHTML = '<a class="button" href="#">' + I18n.t('js.maps.marker.ride.associate_poi') + '</a>';
            //
            // const popupPlannedButton = document.createElement('div');
            // popupPlannedButton.id = 'poi-plan-planned';
            // popupPlannedButton.className = 'details-link';
            // popupPlannedButton.innerHTML = '<a class="button" href="#">' + I18n.t('js.maps.marker.ride.planned_poi') + '</a>';
            //
            // popupContentElement.appendChild(popupPerformedButton);
            // popupContentElement.appendChild(popupPlannedButton);
        }

        const popupEditButtonContainer = document.createElement('div');
        popupEditButtonContainer.className = 'details-link';
        const popupEditButton = document.createElement('button');
        popupEditButton.id = this._mapModel.poiEditButtonId;
        popupEditButton.className = 'button-outlined';
        popupEditButton.href = '#';
        popupEditButton.dataset.markerId = markerPoint.markerId;
        popupEditButton.innerHTML = I18n.t('js.maps.marker.poi.edit');
        popupEditButtonContainer.appendChild(popupEditButton);
        popupContentElement.appendChild(popupEditButtonContainer);

        popupContainer.appendChild(popupContentElement);

        return popupContainer.outerHTML;
    };

    _addPoiPopupMarkerEvents = () => {
        this._model.$mapId.addEventListener('click', (event) => {
            if (!event.target) {
                return;
            }

            if (event.target.matches('#' + this._mapModel.poiEditButtonId)) {
                event.preventDefault();

                const poiMarkerId = parseInt(event.target.getAttribute('data-marker-id'));
                if (poiMarkerId) {
                    const poiMarker = this._mapModel.poiMarkersInfo.find((markerInfo) => markerInfo.type === 'planPoi' && markerInfo.data.id === poiMarkerId);
                    if (poiMarker) {
                        this._poiEditClickedFuncs(poiMarker.data);
                    }
                }
            }
        });
    };

    // _poiMarkerClickedFuncs = (markerType, markerData) => {
    //     for (let i = 0, n = this._mapModel.poiMarkerClicked.length; i < n; i++) {
    //         this._mapModel.poiMarkerClicked[i](markerType, markerData);
    //     }
    // };

    _removePoiMarker = (markerPoint, markerInfo /*, markerIndex */) => {
        this.alertPopup.fire({
            title: I18n.t('js.maps.poi.plan.delete'),
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: I18n.t('js.helpers.buttons.cancel'),
            confirmButtonText: I18n.t('js.helpers.buttons.apply')
        })
            .then(function (result) {
                if (result.isConfirmed) {
                    // markerPoint.marker.remove();
                    // this._model.markersInfo.splice(markerIndex, 1);
                    // this._model.poiPointMarkers = this._model.poiPointMarkers.filter(function (poiPointMarker) {
                    //     return poiPointMarker.data.id !== markerInfo.data.id;
                    // });
                    //
                    // this._drawMarkersPoints();

                    this._poiRemovedClickedFuncs(markerInfo.data);
                }
            }.bind(this));
    };

    _clearPlanPoiMarkersPoints = () => {
        if (!this._model.map) {
            return;
        }

        this._removeMarkersPoints();
        this._removeMarkersInfo();
        // this._removePoiPointMarkers(options);
    };

    _removeMarkersPoints = () => {
        for (let i = 0; i < this._mapModel.poiMarkersPoints.length; i++) {
            this._mapModel.poiMarkersPoints[i].marker.remove();
        }
        this._mapModel.poiMarkersPoints = [];
    };

    _removeMarkersInfo = () => {
        this._mapModel.poiMarkersInfo = [];
    };

    _fitMapBounds = () => {
        if (!this._model.map) {
            return;
        }

        // get the map bounds by looking at all polylines involved
        if (this._model.mapBounds !== null) {
            return;
        }

        let bounds = this._buildBounds();

        if (this._mapModel.poiPointMarkers.length > 0) {
            for (let p = 0; p < this._mapModel.poiPointMarkers.length; p++) {
                bounds = bounds.extend(this._mapModel.poiPointMarkers[p].latLng);
            }
        }

        if (this._mapModel.rideStartMarker) {
            bounds = bounds.extend(this._mapModel.rideStartMarker.marker.getLngLat());
        }

        if (Utils.isPresent(this._model.includeBounds) && this._model.includeBounds.length === 4) {
            const bbox = [
                [
                    parseFloat(this._model.includeBounds[1]),
                    parseFloat(this._model.includeBounds[0])
                ],
                [
                    parseFloat(this._model.includeBounds[3]),
                    parseFloat(this._model.includeBounds[2])
                ]
            ];
            bounds = bounds.extend(bbox);
        }

        // fit to the overall bounds
        const fitBoundsOptions = {
            // Prevent map zoom on initial map loading
            zoom: this._model.minZoom,
            padding: this._mapModel.rideStartMarker ? 100 : 20
        };

        this._model.mapBounds = bounds;
        this.fitBounds(this._model.mapBounds, fitBoundsOptions);

        // Set minimum zoom
        if (this._model.map.getZoom() > (this._model.minZoom || 16)) {
            this._model.map.setZoom(this._model.minZoom || 16);
        }
    };

    // Called by MapInterface
    _onMapStyleChanged = () => {
        this._removeMarkersPoints();
        this._drawPoiMarkersPoints();

        if (this.hasModule('RIDE_TRACK')) {
            this.resetRidesTracksStyle();
        }
    };
}
